import { Box, Card, CardContent, CardHeader, Chip, Grid, Typography } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';
import { UserLabel } from '../../../../../../../../../components/users/UserLabel';
import { reportNameMessages } from '../../../../../../reports/constants';
import {
  activeStatusMessages,
  aodRuleWizardMessages,
  aodRulesMessages,
  notificationPolicyMessages,
  triggerMessages,
} from '../../../../../messages';
import { AodRuleWizardValues } from '../../../types';

interface BasicInfoReviewProps {
  basic: AodRuleWizardValues['basic'];
}

export const BasicInfoReview: React.FC<BasicInfoReviewProps> = ({ basic }) => {
  const { formatMessage } = useIntl();

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title={formatMessage(aodRuleWizardMessages.reviewBasicInfo)} />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {formatMessage(aodRulesMessages.name)}
            </Typography>
            <Typography variant="body1">{basic.name || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {formatMessage(aodRulesMessages.trigger)}
            </Typography>
            <Typography variant="body1">
              {basic.trigger
                ? formatMessage(
                    triggerMessages[basic.trigger] ?? {
                      id: `audit.aod.rules.trigger.${basic.trigger}`,
                      defaultMessage: basic.trigger,
                    },
                  )
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {formatMessage(aodRulesMessages.reportType)}
            </Typography>
            <Typography variant="body1">
              {basic.reportType ? formatMessage(reportNameMessages[basic.reportType]) : '-'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {formatMessage(aodRulesMessages.active)}
            </Typography>
            <Chip
              label={
                basic.active
                  ? formatMessage(activeStatusMessages.active)
                  : formatMessage(activeStatusMessages.inactive)
              }
              color={basic.active ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />
          </Grid>
          {basic.notificationSettings && (
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {formatMessage(notificationPolicyMessages.label)}
              </Typography>
              <Typography variant="body1">
                {formatMessage(
                  notificationPolicyMessages[
                    basic.notificationSettings.policy as keyof typeof notificationPolicyMessages
                  ] ?? {
                    id: `audit.aod.rules.notificationPolicy.${basic.notificationSettings.policy}`,
                    defaultMessage: basic.notificationSettings.policy,
                  },
                )}
              </Typography>
            </Grid>
          )}
        </Grid>

        {!!basic.managers?.length && (
          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {formatMessage(aodRuleWizardMessages.managers)}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {basic.managers.map((manager, index) => (
                <Chip key={`${manager.id}-${index}`} label={<UserLabel id={manager.id} />} />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
